import {isFeatureEnabled} from '@github-ui/feature-flags'
import type {RelayCompat} from '@github-ui/server-persisted-queries/relay-compat'
import {useShouldShowCopilotFunctionalityInIssues} from '@github-ui/use-should-show-copilot-functionality-in-issues'
import type {ReactNode} from 'react'
import {graphql, useFragment} from 'react-relay'

import type {RolledUpTimelineItem} from '../../utils/timeline-rollups'
import type {IssueViewerIssue$data} from '../__generated__/IssueViewerIssue.graphql'
import type {IssueSecondaryNode} from '../issue-viewer-secondary-query-types'
import type {
  IssueTimelineIssueFragment$data,
  IssueTimelineIssueFragment$key,
} from './__generated__/IssueTimelineIssueFragment.graphql'
import type {EventProps, IssueTimelineItemFragment} from './IssueTimelineItem'
import type {LoadMoreCallbackFn} from './LoadMore'
import {TimelinePanel} from './TimelinePanel'
import {useTimelineItems} from './use-timeline-items'
import type {UseTimelineItemsCoreResult} from './use-timeline-items-core'
import {useTimelineItemsTanstack} from './use-timeline-items-tanstack'

type LoadItem = {
  type: 'load'
  position: 'top' | 'bottom'
  loadFromTop: LoadMoreCallbackFn
  loadFromBottom: LoadMoreCallbackFn
  numberOfRemainingItems: number
  isCurrentlyLoading?: boolean
}

type EventItem = {type: 'event'} & RolledUpTimelineItem<IssueTimelineItemFragment>
export type TimelineItem = EventItem | LoadItem

/**
 * Temporary type for parsing the timeline items and grouping them into section elements
 * for improve the accessibility of the event items by adding landmarks for navigation.
 */
export type RenderItem = {
  /**
   * Controls whether the item has already been added to the grouped events or not.
   *
   * Only used in events and not in load items, graceful degratation items or issue comments.
   */
  isAddedToGroupedEvents: boolean
  timelineItem: TimelineItem
  render: ReactNode
}

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
const mainTimelineFragment = graphql`
  fragment IssueTimelineIssueFragment on Issue
  @argumentDefinitions(skip: {type: "Int"}, count: {type: "Int", defaultValue: 15}) {
    id
    url
    repository {
      id
      nameWithOwner
      visibility
      viewerCanPush
    }
    ...useTimelineItemsFrontFragment @arguments(count: $count, skip: $skip)
    ...useTimelineItemsBackFragment
  }
`

export type IssueTimelineSecondaryFragment = RelayCompat<Pick<IssueSecondaryNode, 'isTransferInProgress'>>

type IssueTimelineProps = {
  issue: IssueViewerIssue$data
  issueSecondary?: IssueTimelineSecondaryFragment
  highlightedEvent: string | undefined
  page?: number
  // Passed rather than selected: adding them to the fragment changes `IssueViewerViewQuery` and
  // invalidates every cached issue, and the caller already holds all three.
  owner: string
  repo: string
  number: number
} & EventProps
export const IssueTimeline = ({
  issue,
  issueSecondary,
  viewer,
  highlightedEvent,
  page,
  owner,
  repo,
  number,
  onCommentChange,
  onCommentReply,
  onCommentEditCancel,
  optionConfig,
}: IssueTimelineProps) => {
  const data = useFragment<IssueTimelineIssueFragment$key>(mainTimelineFragment, issue)

  const showCopilotMenuItem = useShouldShowCopilotFunctionalityInIssues({
    repositoryVisibility: data.repository.visibility,
    userCanPushToRepo: data.repository.viewerCanPush,
  })

  return (
    <ClassicTimeline
      data={data}
      owner={owner}
      repo={repo}
      number={number}
      highlightedEvent={highlightedEvent}
      page={page}
      showCopilotMenuItem={showCopilotMenuItem}
      isTransferInProgress={issueSecondary?.isTransferInProgress}
      viewer={viewer}
      onCommentChange={onCommentChange}
      onCommentReply={onCommentReply}
      onCommentEditCancel={onCommentEditCancel}
      optionConfig={optionConfig}
    />
  )
}

/**
 * Classic (non-tabbed) timeline. Separated into its own component so that the
 * `useTimelineItems` hook (which depends on the classic fragments) is only
 * mounted when those fragments are present in the response.
 */
type ClassicTimelineProps = {
  data: IssueTimelineIssueFragment$data
  owner: string
  repo: string
  number: number
  highlightedEvent: string | undefined
  page?: number
  showCopilotMenuItem?: boolean
  isTransferInProgress?: boolean
} & EventProps
const ClassicTimeline = ({
  data,
  owner,
  repo,
  number,
  viewer,
  highlightedEvent,
  page,
  onCommentChange,
  onCommentReply,
  onCommentEditCancel,
  optionConfig,
  showCopilotMenuItem,
  isTransferInProgress,
}: ClassicTimelineProps) => {
  // `page` stays on Relay: `?timeline_page=N` is served by selecting 150 rows after an offset, and
  // the timeline endpoint has no offset argument -- the TanStack path would silently render the
  // first page for every crawler URL.
  const useTanstack = isFeatureEnabled('issue_viewer_paved_path') && !page
  const TimelineSource = useTanstack ? TanstackTimeline : RelayTimeline

  return (
    <TimelineSource
      data={data}
      owner={owner}
      repo={repo}
      number={number}
      viewer={viewer}
      highlightedEvent={highlightedEvent}
      page={page}
      onCommentChange={onCommentChange}
      onCommentReply={onCommentReply}
      onCommentEditCancel={onCommentEditCancel}
      optionConfig={optionConfig}
      showCopilotMenuItem={showCopilotMenuItem}
      isTransferInProgress={isTransferInProgress}
    />
  )
}

const RelayTimeline = (props: ClassicTimelineProps) => {
  const result = useTimelineItems({
    timelineData: props.data,
    highlightedEvent: props.highlightedEvent,
    page: props.page,
  })

  return <TimelineItemsPanel {...props} {...result} />
}

const TanstackTimeline = (props: ClassicTimelineProps) => {
  const result = useTimelineItemsTanstack({
    owner: props.owner,
    repo: props.repo,
    number: props.number,
    issueId: props.data.id,
    highlightedEvent: props.highlightedEvent,
  })

  return <TimelineItemsPanel {...props} {...result} />
}

type TimelineItemsPanelProps = ClassicTimelineProps & UseTimelineItemsCoreResult

const TimelineItemsPanel = ({
  data,
  viewer,
  highlightedEvent,
  page,
  onCommentChange,
  onCommentReply,
  onCommentEditCancel,
  optionConfig,
  showCopilotMenuItem,
  isTransferInProgress,
  timelineItems,
  totalItemCount,
  highlightedReady,
}: TimelineItemsPanelProps) => {
  return (
    <TimelinePanel
      timelineItems={timelineItems}
      totalItemCount={totalItemCount}
      highlightedEvent={highlightedEvent}
      highlightedReady={highlightedReady}
      issueId={data.id}
      repositoryId={data.repository.id}
      repositoryNameWithOwner={data.repository.nameWithOwner}
      issueUrl={data.url}
      page={page}
      isTransferInProgress={isTransferInProgress}
      showCopilotMenuItem={showCopilotMenuItem}
      viewer={viewer}
      onCommentChange={onCommentChange}
      onCommentReply={onCommentReply}
      onCommentEditCancel={onCommentEditCancel}
      optionConfig={optionConfig}
    />
  )
}
